import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography } from '@mui/material';
import { VK_ENTER } from 'app/appConstants';
import { FileThumbnaildGetDto, RequestLifeCycle } from "app/appModels";
import { MprcButton, MprcChipChoice, MprcConfirmDialog, MprcDialogTitleClose, MprcFormSubHeader, MprcIconButton, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonDelete, ButtonSave } from 'components/buttons';
import { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from "components/FileUpload/AttachSelection";
import { IconButtonClose } from "components/iconButtons";
import { IconButtonExpandScreen } from "components/iconButtons/IconButtonExpandScreen";
import { IconAdd, IconEdit } from 'components/icons';
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { TopicAutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical/AutoCompleteHierarchicalModel";
import { QuillCommands } from "components/Quill";
import { AsyncUploadResult, CreateOrUpdateBinderCommand, GetBinderPublicationDto, GetPublicationAttachDto, PublicationApprovalStatus, PublicationAttachFileItem, PublicationAttachType, PublicationCreationSettingsResponse, PublicationTagItem, PublicationType, TopicAutoCompleteHierarchicalModelDto } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from "providers/SessionProvider";
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { NIL, v4 as uuidv4 } from 'uuid';
import { publish } from "views/knowledge/feed/FeedEvents";
import { ICreatePublicationProps } from "views/knowledge/publication/creation";
import {
    IInstitutionalBoxValidation,
    IPublicationChannelBoxValidation,
    PublicationAdvancedBox,
    PublicationAttachFileHandler,
    PublicationAttachView,
    PublicationChannelBox,
    PublicationEditor,
    PublicationTopicsBox
} from "views/knowledge/publication/creation/components";
import { PublicationDialogContent } from "views/knowledge/publication/creation/components/PublicationDialogContent";
import { IStickerBoxValidation } from "views/knowledge/publication/creation/components/PublicationStickerBox";
import { parseContentTopics } from "views/knowledge/publication/creation/CreatePost";


///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface ISection {
    id: string;
    name: string;
    content: string;
}

interface IValidation {
    title?: boolean;
    description?: boolean;
    editJustification?: boolean;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IEditCardProps {
    mode: "new" | "edit";
    name: string;
    onDelete?: () => void;
    onClose: (name?: string) => void;
}

const EditCard = ({ mode, name, onDelete, onClose }: IEditCardProps) => {
    const { t } = useTranslation("knowledge");
    const [text, setText] = useState<string>(name ?? "");
    const [deleting, setDeleting] = useState<boolean>(false);

    return (
        <Dialog open maxWidth="xs" fullWidth>
            <MprcDialogTitleClose
                shrinkPadding
                title={name == null ? t("Publication.CreateBinder.NewCard") : t("Publication.CreateBinder.EditCard")}
                onClose={() => onClose()}
            />
            <DialogContent>
                <MprcConfirmDialog
                    open={deleting}
                    message={t("Publication.CreateBinder.ConfirmDeleteMessage")}
                    onClose={(confirm) => {
                        setDeleting(false);
                        if (confirm) onDelete!();
                    }}
                />
                <TextField
                    autoFocus
                    fullWidth
                    name="title"
                    margin="normal"
                    label={t("Publication.CreateBinder.NameLabel")}
                    autoComplete="off"
                    tabIndex={0}
                    value={text}
                    error={text.length == 0}
                    slotProps={{
                        input: {
                            inputProps: { maxLength: 255 }
                        }
                    }}
                    onKeyDown={(e) => e.key == VK_ENTER && onClose(text)}
                    onChange={(e) => setText(e.currentTarget.value)}
                />
            </DialogContent>
            <DialogActions>
                <Grid container justifyContent={"space-between"} alignItems={"center"} width="100%">
                    <Grid>
                        {mode == "edit" && onDelete &&
                            <ButtonDelete
                                variant="contained"
                                showIcon={false}
                                onClick={() => setDeleting(true)}
                            />
                        }
                    </Grid>
                    <Grid>
                        <ButtonCancel onClick={() => onClose()} />
                        <span>&nbsp;</span>
                        <ButtonSave onClick={() => onClose(text)} />
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreateBinder = (props: ICreatePublicationProps) => {
    const { onClose } = props;
    const minHeight = 200;
    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();
    const session = useSession();

    const editorRef = useRef<QuillCommands | null>(null);
    const config = useRef<PublicationCreationSettingsResponse | null>(null);

    const [lastError, setLastError] = useState<any>();
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [fullScreenMode, setFullScreenMode] = useState<boolean>(false);
    const [loadDto, setLoadDto] = useState<GetBinderPublicationDto>();
    const [loadPagesDto, setLoadPagesDto] = useState<ISection[]>([]);
    const [contentTopics, setContentTopics] = useState<TopicAutoCompleteHierarchicalModel[]>([]);
    const [recordChanged, setRecordChanged] = useState<boolean>(false);
    const [uploadingStatus, setUploadingStatus] = useState<UploadingStatus>("none");
    const [validation, setValidation] = useState<IValidation & IInstitutionalBoxValidation & IPublicationChannelBoxValidation & IStickerBoxValidation>
        ({
            title: undefined,
            description: undefined,
            channel: undefined,
            period: true,
            viewStickerUntil: true,
            editJustification: true,
        });

    const [pages, setPages] = useState<ISection[]>([]);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [dialog, setDialog] = useState<any>(undefined);

    const [step, setStep] = useState<number>(1);
    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;
    const StepThrirdPage = 3;

    const [record, setRecord] = useState<GetBinderPublicationDto>(
        {
            publicationId: NIL,
            content: "",
            title: "",
            topics: [],
            attachs: [],
            advancedOptions: {
                isInstitutional: false,
                viewPeriodStart: null,
                viewPeriodEnd: null,
                viewStickerUntil: null,
                sticker: null,
                tags: []
            },
            isDraft: false,
            channel: null!,
            publicationType: PublicationType.Binder,
            presentation: null,
            editJustification: null,
        });

    ///////////////////////////////////////////////////////////////////////
    /// Manuseio do Registro
    ///////////////////////////////////////////////////////////////////////

    ///
    const updateRecord = (data: Partial<GetBinderPublicationDto>) => {
        return { ...record, ...data };
    };

    ///
    const validateDescription = (html: string) => {
        return (html != null && html != "<p><br></p>");
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        setRecord(state => ({ ...state, title: v }));
        setValidation(state => ({ ...state, title: v.length > 0 }));
    };

    ///
    const updatePageContent = (page: number, content: string) => {
        setPages(pages => pages.map((p, index) => (index != page) ? p : { ...p, content }));
        setValidation(state => ({ ...state, description: validateDescription(content) }));
    };


    ///////////////////////////////////////////////////////////////////////
    /// Manuseio das Fichas/Páginas da Publicação
    ///////////////////////////////////////////////////////////////////////

    ///
    const addPage = (id: string | null, name: string, content: string) => {
        setPages(pages => [...pages, { id: id ?? uuidv4(), name, content }]);
        setValidation(state => ({ ...state, description: undefined }));
    };

    ///
    const deletePage = (page: ISection) => {
        const currentIndex = pages.findIndex(x => x.id == page.id);

        setPages(pages => pages.filter(x => x.id != page.id));

        setRecord(state => ({
            ...state,
            attachs: state.attachs.filter(x => x.binderPageRefId != page.id),
        }));

        setValidation(state => ({ ...state, description: true }));

        setCurrentPage(currentIndex == 0 ? 0 : currentIndex - 1);
    };

    ///
    const handlePage = (page: ISection | null) => {
        setDialog(
            <EditCard
                mode={page == null ? "new" : "edit"}
                name={page?.name ?? t("Publication.CreateBinder.NewCardText", { count: pages.length + 1 })}
                onDelete={
                    pages.length == 1
                        ? undefined
                        : () => {
                            deletePage(page!);
                            setDialog(null);
                        }}
                onClose={(name) => {
                    if (name != null) {
                        if (page == null) {
                            addPage(null, name, "");
                            setCurrentPage(pages.length);
                        } else {
                            setPages(pages => pages.map(x => x.id == page!.id ? { ...x, name: name } : x));
                        }
                    }
                    setDialog(null);
                }}
            />
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Controle de Upload
    ///////////////////////////////////////////////////////////////////////

    ///
    const handleFileUploaded = (e: AsyncUploadResult) => {
        const data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        const attachType: PublicationAttachType = (PublicationAttachType[e.mainFile.metadata.type as any]) as any;
        const attachs = record.attachs.splice(0);

        var at = {
            attachId: uuidv4(),
            binderPageRefId: pages[currentPage].id,
            file: {
                fileId: data.mainFile.fileId,
                metadata: data.mainFile.metadata,
                mimetype: data.mainFile.mimetype,
                url: data.mainFile.url
            },
            thumbnail: data.thumbnail == null
                ? null
                : {
                    fileId: data.thumbnail.fileId,
                    metadata: data.thumbnail.metadata,
                    mimetype: data.thumbnail.mimetype,
                    url: data.thumbnail.url
                },
            attachType: attachType,
            isEmbed: false,
            linkMetadata: null,
            videoProcessed: false,
            caption: null,
        } as GetPublicationAttachDto;

        attachs.push(at);

        const newTitle = record.title == "" ? Formaters.capitalizedTitle(LibUtils.getFilenameWithoutExtension(data.mainFile.metadata.filename)) ?? record.title : record.title;

        setRecord(state => ({
            ...state,
            attachs: attachs,
            presentation: null,
            title: newTitle
        })
        );

        setValidation(state => ({ ...state, title: newTitle.length > 0 }));
    };

    ///
    const handleUploadingStatus = (status: UploadingStatus) => {
        setUploadingStatus(status);
    };

    ///////////////////////////////////////////////////////////////////////
    /// Load / Save
    ///////////////////////////////////////////////////////////////////////

    ///
    const setDefaultValues = () => {
        if (config.current && config.current.defaultChannel.length > 0) {
            const channel = config.current.defaultChannel[0];
            setRecord(state => ({
                ...state,
                channel: {
                    channelId: channel.key,
                    name: channel.label,
                    isPrivate: channel.isPrivate,
                    requirePublicationEditJustification: channel.requirePublicationEditJustification,
                    thumbnailUrl: channel.thumbnailUrl!
                }
            }));

            setValidation(state => ({ ...state, channel: true }));
        }

        if (config.current && config.current.topics.length > 0) {
            setRecord(state => ({
                ...state,
                topics: config.current!.topics
            }));
        }

        if (pages.length == 0) setPages([{ id: uuidv4(), name: t("Publication.CreateBinder.NewCardText", { count: 1 }), content: "" }]);
    };

    ///
    const readPages = (htmlString: string): ISection[] => {
        const parser = new DOMParser();
        const doc: Document = parser.parseFromString(htmlString, 'text/html');
        const sections = doc.querySelectorAll("section");

        const pages: ISection[] = [];

        for (var i = 0; i < sections.length; i++) {

            const id = sections[i].getAttribute("id") ?? uuidv4();
            const name = sections[i].getAttribute("name") ?? t("Publication.CreateBinder.NewCardText", { count: i + 1 });
            pages.push({ id, name, content: sections[i].getHTML() });
        }

        return pages;
    };

    ///
    const writePages = (): string => {
        let result = "";
        for (var i = 0; i < pages.length; i++) {
            result += `<section id="${pages[i].id}" name="${pages[i].name}">${pages[i].content}</section>`;
        }
        return result;
    };

    ///
    const loadSettings = async () => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: props.channelId ?? null, defaultTopicId: props.topicId ?? null, exclusiveListId: null })
            .then((result) => {
                config.current = result.data;
                setDefaultValues();
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);

        Promise.all([
            api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: null, exclusiveListId: null }),
            api.v1.knowledgePublication.getBinderPublication({ publicationId }),
        ])
            .then((results) => {
                config.current = results[0].data;

                const record = results[1].data;
                setRecord(record);
                setLoadDto(LibUtils.deepCopy(record));
                setValidation({
                    channel: true,
                    title: true,
                    description: true,
                    period: true,
                    editJustification: !record.channel.requirePublicationEditJustification
                });

                var pages = readPages(record.content!);
                setPages(pages);
                setCurrentPage(0);
                setLoadPagesDto(pages);

                setFormCycle(RequestLifeCycle.None);
                setUploadingStatus("none");
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const save = (isDraft: boolean) => {

        setFormCycle(RequestLifeCycle.Posting);

        const files: PublicationAttachFileItem[] = record.attachs.map((v) => ({
            attachId: v.attachId,
            fileId: v.file?.fileId ?? null,
            thumbnailId: v.thumbnail?.fileId ?? null,
            cardImageId: v.cardImage?.fileId ?? null,
            linkMetadata: v.linkMetadata,
            caption: v.caption,
            binderPageRefId: v.binderPageRefId,
        }));

        const dto: CreateOrUpdateBinderCommand = {
            publicationId: record.publicationId,
            content: writePages(),
            title: record.title,
            advancedOptions: {
                isInstitutional: record.advancedOptions.isInstitutional,
                stickerId: record.advancedOptions.sticker?.stickerId ?? null,
                viewStickerUntil: record.advancedOptions.viewStickerUntil,
                viewPeriodEnd: record.advancedOptions.viewPeriodEnd,
                viewPeriodStart: record.advancedOptions.viewPeriodStart,
                tags: record.advancedOptions.tags.map((tag) => ({ tagId: tag.tagId, tagName: tag.tagName } as PublicationTagItem))
            },
            isDraft: isDraft,
            attachs: files,
            presentation: record.presentation,
            topics: record.topics.map((item) => item.key),
            channelId: record.channel?.channelId,
            threadId: null,
            contentUnformatted: null,
            editJustification: record.editJustification,
        };

        api.v1.knowledgePublication.createOrUpdateBinderPublication(dto)
            .then((response) => {
                onClose(response.data);
                setFormCycle(RequestLifeCycle.None);

                if (response.data.newPublicationId) {

                    if (!isDraft) {
                        if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                            publish("PublicationCreated", response.data.newPublicationId);
                            publish("AuthorNewPublication", session.user.userId);
                            publish("ChannelNewPublication", dto.channelId);
                            dto.topics.forEach(topicId => publish("TopicNewPublication", topicId));

                            enqueueSnackbar(
                                t("Publication.CreatePost.PublishMessage")
                                , {
                                    variant: "mprcAction",
                                    action: <Link to={KnowledgePaths.PublicationViewerResolver(response.data.newPublicationId, PublicationType.Post)}>{t("Publication.Messages.OpenPublication")}</Link>
                                }
                            );
                        }
                        else {
                            enqueueSnackbar(t("Publication.CreatePost.PublishModerationMessage"), { variant: "warning" });
                        }
                    }
                    else {
                        publish("PublicationDraftCreated", response.data.newPublicationId);
                        enqueueSnackbar(t("Publication.CreatePost.PublishDraftMessage"), { variant: "info" });
                    }
                }
                else {
                    if (response.data.success == true) {
                        if (response.data.approvalStatus == null || response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                            publish("PublicationUpdate", record.publicationId);
                            enqueueSnackbar(t("Publication.CreatePost.PublishUpdateMessage"), { variant: "success" });
                        }
                        else {
                            enqueueSnackbar(t("Publication.CreatePost.PublishModerationUpdateMessage"), { variant: "warning" });
                        }
                    }
                    else {
                        enqueueSnackbar(t("shared:Record.FailSuccessMessage"), { variant: "error" });
                    }
                }
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///////////////////////////////////////////////////////////////////////
    /// Controle de Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    const goStep2 = () => {
        const currentTopics = record.topics;

        const topics = parseContentTopics(record.content ?? "", currentTopics as TopicAutoCompleteHierarchicalModel[]);

        topics.filter(p => p.selectable).forEach((topic) => {
            if (currentTopics.find(p => p.key == topic.key) == null && currentTopics.length < 5) {
                currentTopics.push(topic as TopicAutoCompleteHierarchicalModelDto);
                topic.selectable = false;
            }
        });

        setContentTopics(topics);
        updateRecord({ topics: currentTopics });

        setStep(2);
    };

    ///
    const canGoStep2 = () => {
        return (uploadingStatus != "uploading" && validation.title === true && validation.description === true);
    };

    ///
    const goStep3 = () => {
        setStep(3);
    };

    ///
    const canGoStep3 = () => {
        return (validation.channel != null);
    };

    ///
    const canSave = (): boolean => {
        return canGoStep2()
            && validation.channel === true
            && validation.period === true
            && (validation.viewStickerUntil == undefined || validation.viewStickerUntil == true)
            && (recordChanged || record.isDraft)
            && (!record.channel.requirePublicationEditJustification || validation.editJustification === true);
    };

    ///////////////////////////////////////////////////////////////////////
    /// Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    steps[StepFirstPage] = () => {
        const pageKey = `page-${currentPage}`;

        return (
            <>
                <PublicationDialogContent>

                    <TextField
                        autoFocus
                        fullWidth
                        name="title"
                        margin="normal"
                        placeholder={t("Publication.CreateBinder.TitlePlaceholder")}
                        autoComplete="off"
                        tabIndex={0}
                        value={record.title}
                        error={validation.title === false}
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 255 }
                            }
                        }}
                        onChange={(e) => handleTitleChange(e)}
                    />

                    <Stack direction="row" alignContent="start" alignItems="start">
                        <MprcIconButton
                            sx={{ p: 0, pt: .25 }}
                            disabled={validation.description !== true}
                            onClick={() => handlePage(null)}
                        >
                            <IconAdd />
                        </MprcIconButton>
                        <Stack direction="row" flexWrap={"wrap"} alignContent="start" alignItems="center">
                            {
                                pages.map((item, index) => {
                                    return <MprcChipChoice
                                        key={item.id}
                                        label={item.name}
                                        selected={index == currentPage}
                                        deleteIcon={<IconEdit />}
                                        onClick={validation.description === true ? () => setCurrentPage(index) : undefined}
                                        onDelete={validation.description === true || index == currentPage ? () => handlePage(item) : undefined}
                                    />;
                                })
                            }
                        </Stack>
                    </Stack>

                    <PublicationEditor
                        key={pageKey}
                        minHeight={minHeight}
                        autoFocus={pages.length > 1}
                        toolbar={true}
                        readOnly={false}
                        initialValue={pages.length == 0 ? "" : pages[currentPage].content}
                        disableInitialValueMonitoring={true}
                        placeholder={t("Publication.CreatePost.EditorPlaceholder")}
                        onChange={(_, __, ___, editor) => {
                            setValidation(state => ({
                                ...state,
                                description: validateDescription(editor.getHTML())
                            }));
                        }}
                        onBlur={(_, html) => updatePageContent(currentPage, html ?? "")}
                        error={validation.description === false}
                        ref={editorRef}
                    />

                    <PublicationAttachView
                        key={`${pageKey}-AttachView`}
                        forceAttachsInList={true}
                        attachs={record.attachs.filter(p => p.binderPageRefId == pages[currentPage].id)}
                        onChange={
                            (attachs) => {
                                const others = record.attachs.filter(p => p.binderPageRefId != pages[currentPage].id);
                                setRecord(state => ({
                                    ...state,
                                    attachs: [...others, ...attachs]
                                }));
                            }
                        }
                    />

                    <PublicationAttachFileHandler
                        key={`${pageKey}-FileHandler`}
                        publicationId={record.publicationId}
                        attachs={record.attachs}
                        readOnly={validation.description !== true}
                        allowAttachVideo={false}
                        allowAttachAudio={false}
                        allowAttachImage={false}
                        dropAreaMessage={t("Publication.CreateBinder.DropAreaMessage")}
                        maxSize={config.current?.maxAttachSize ?? undefined}
                        onUploadComplete={handleFileUploaded}
                        onUploadStatusChange={handleUploadingStatus}
                    />
                </PublicationDialogContent >

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                            <b>{t("Publication.CreateBinder.Footer1")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{t("Publication.CreateBinder.Footer2")}</span>
                        </Grid>

                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="primary"
                                disabled={!canGoStep2()}
                                onClick={goStep2}
                            >
                                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        return (
            <>
                <DialogContent>

                    <PublicationChannelBox
                        channel={record.channel}
                        suggestions={config.current!.channelSuggestions}
                        onUpdateState={
                            ({ channel, validation }) => {
                                setRecord(state => ({ ...state, channel: channel! }));
                                setValidation(state => ({
                                    ...state,
                                    channel: true,
                                    editJustification: channel?.requirePublicationEditJustification == true,
                                }));
                            }
                        }
                        validation={validation}
                    />

                    <PublicationTopicsBox
                        channelId={record.channel?.channelId}
                        topics={record.topics as AutoCompleteHierarchicalModel[]}
                        suggestions={contentTopics}
                        publication={{ title: record.title, content: record.content }}
                        onUpdateState={
                            (topics) => setRecord(state => ({
                                ...state,
                                topics: topics as TopicAutoCompleteHierarchicalModelDto[]
                            }))
                        }
                    />

                    <PublicationAdvancedBox
                        channelId={record.channel?.channelId}
                        allowInstitutionalPublisher={config.current?.allowInstitutionalPublisher ?? false}
                        canCreateTags={config.current?.canCreateTags ?? false}
                        onUpdateState={({ advancedOptions, validation }) => {
                            setRecord(state => ({
                                ...state,
                                advancedOptions: { ...advancedOptions }
                            }));
                        }}
                        advancedOptions={{ ...record.advancedOptions }}
                        validation={validation}
                    />
                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2} width="100%">
                        <Grid>
                            {
                                (record.publicationId == NIL || record.isDraft) &&
                                <MprcButton
                                    variant="contained"
                                    color="warning"
                                    onClick={() => save(true)}
                                    disabled={!canSave()}
                                >
                                    <span>{t("Publication.Common.Form.ButtonDraftText")}</span>
                                </MprcButton>
                            }
                        </Grid>

                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={() => setStep(1)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            {
                                record.channel?.requirePublicationEditJustification == true && recordChanged
                                    ? <MprcButton
                                        variant="contained"
                                        color="primary"
                                        disabled={!canGoStep3()}
                                        onClick={goStep3}
                                    >
                                        <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
                                    </MprcButton>
                                    : (recordChanged || record.isDraft) && <MprcButton
                                        variant="contained"
                                        color="success"
                                        onClick={() => save(false)}
                                        disabled={!canSave()}
                                    >
                                        <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                                    </MprcButton>
                            }
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    steps[StepThrirdPage] = () => {
        return (
            <>
                <DialogContent>
                    <MprcFormSubHeader
                        title={t("Publication.CreatePost.EditJustificationTitle") + " *"}
                        subtitle={t("Publication.CreatePost.EditJustificationSubtitle")}
                        margin="dense"
                    />
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        name="editJustification"
                        margin="dense"
                        variant="outlined"
                        rows={5}
                        placeholder={t("Publication.CreatePost.EditJustificationHint")}
                        required
                        slotProps={{
                            input: {
                                inputProps: { maxLength: 2048 }
                            }
                        }}
                        value={record.editJustification ?? ""}
                        error={validation.editJustification == false}
                        onChange={(e) => {
                            setRecord(state => ({
                                ...state,
                                editJustification: e.target.value
                            }));
                            setValidation(state => ({ ...state, editJustification: e.target.value.length > 0 }));
                        }
                        }
                    />
                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="end">
                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={() => setStep(2)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color="success"
                                onClick={() => save(false)}
                                disabled={!canSave()}
                            >
                                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(record, loadDto) || !LibUtils.deepCompare(pages, loadPagesDto);
        setRecordChanged(isRecordChanged);
    }, [record, pages]);

    // Ao carregar registro para edição
    useEffect(() => {
        if (props.publicationId)
            load(props.publicationId);
        else
            loadSettings();
    }, []);

    ///////////////////////////////////////////////////////////////////////
    /// Render
    ///////////////////////////////////////////////////////////////////////
    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
            fullScreen={fullScreenMode}
            sx={fullScreenMode ? { "& .MuiPaper-root": { maxHeight: "90%", maxWidth: "90%" } } : undefined}
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t(record.publicationId == NIL ? "Publication.CreateBinder.TitleNew" : "Publication.CreateBinder.TitleEdit")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonExpandScreen
                            expanded={fullScreenMode}
                            size="small"
                            onClick={() => setFullScreenMode(!fullScreenMode)}
                        />
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />

            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {dialog}

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />

                            {pages.length > 0 && steps[step]()}
                        </>
                    )
            }

        </Dialog>
    );
};
